import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    SignOut,
    ArrowSquareOut,
    Plus,
    PencilSimple,
    Trash,
    FilePdf,
    Newspaper,
    Tag,
    Ticket,
    Trophy,
    GearSix,
    UploadSimple,
    Images,
    Confetti,
    UsersThree,
} from "@phosphor-icons/react";
import { api, photoImageUrl, eventPosterUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { frDate, humanSize } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

function toLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function FieldInput({ field, value, onChange }) {
    const common = "mt-1.5";
    if (field.type === "textarea")
        return <Textarea data-testid={`field-${field.name}`} className={common} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} rows={field.rows || 4} />;
    if (field.type === "switch")
        return (
            <div className="mt-2 flex items-center gap-3">
                <Switch data-testid={`field-${field.name}`} checked={!!value} onCheckedChange={onChange} />
                <span className="text-sm text-slate-500">{value ? "Publié" : "Brouillon"}</span>
            </div>
        );
    if (field.type === "select")
        return (
            <select data-testid={`field-${field.name}`} className={`${common} w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-clay`} value={value ?? field.options[0]} onChange={(e) => onChange(e.target.value)}>
                {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        );
    if (field.type === "datetime")
        return <Input data-testid={`field-${field.name}`} type="datetime-local" className={common} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
    return <Input data-testid={`field-${field.name}`} type={field.type || "text"} className={common} value={value ?? ""} onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)} placeholder={field.placeholder} />;
}

function EntityManager({ title, singular, queryKey, endpoint, fields, empty, defaults, columns }) {
    const qc = useQueryClient();
    const { data: items = [] } = useQuery({ queryKey: [queryKey], queryFn: async () => (await api.get(`/${endpoint}${queryKey === "news" ? "?all=true" : ""}`)).data });
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({});
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toDelete, setToDelete] = useState(null);

    const openNew = () => {
        const f = { ...defaults };
        fields.forEach((fl) => { if (fl.type === "datetime" && f[fl.name]) f[fl.name] = toLocalInput(f[fl.name]); });
        setEditing(null); setForm(f); setOpen(true);
    };
    const openEdit = (it) => {
        const f = { ...it };
        fields.forEach((fl) => { if (fl.type === "datetime" && f[fl.name]) f[fl.name] = toLocalInput(f[fl.name]); });
        setEditing(it); setForm(f); setOpen(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            fields.forEach((fl) => { if (fl.type === "datetime" && payload[fl.name]) payload[fl.name] = new Date(payload[fl.name]).toISOString(); });
            if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, payload);
            else await api.post(`/admin/${endpoint}`, payload);
            toast.success(editing ? "Modifié" : "Ajouté");
            qc.invalidateQueries({ queryKey: [queryKey] });
            setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail ? String(e.response.data.detail) : "Erreur lors de l'enregistrement");
        } finally { setSaving(false); }
    };

    const doDelete = async () => {
        try {
            await api.delete(`/admin/${endpoint}/${toDelete.id}`);
            toast.success("Supprimé");
            qc.invalidateQueries({ queryKey: [queryKey] });
        } catch { toast.error("Erreur lors de la suppression"); }
        setToDelete(null);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-5">
                <h2 className="font-display font-extrabold text-xl text-navy">{title} <span className="font-mono text-sm text-slate-400">({items.length})</span></h2>
                <button onClick={openNew} data-testid={`add-${endpoint}-btn`} className="inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2.5 rounded-md hover:bg-navy-deep transition-colors">
                    <Plus weight="bold" size={16} /> Ajouter
                </button>
            </div>

            {items.length === 0 ? (
                <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">{empty}</div>
            ) : (
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                    {items.map((it) => (
                        <div key={it.id} data-testid={`${endpoint}-row`} className="flex items-center gap-4 p-4">
                            <div className="min-w-0 flex-1">{columns(it)}</div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => openEdit(it)} data-testid={`edit-${endpoint}-btn`} className="grid place-items-center h-9 w-9 rounded-md text-slate-500 hover:bg-slate-100 hover:text-navy transition-colors" aria-label="Modifier">
                                    <PencilSimple size={18} />
                                </button>
                                <button onClick={() => setToDelete(it)} data-testid={`delete-${endpoint}-btn`} className="grid place-items-center h-9 w-9 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors" aria-label="Supprimer">
                                    <Trash size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? `Modifier ${singular}` : `Ajouter ${singular}`}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {fields.map((fl) => (
                            <div key={fl.name}>
                                <Label className="text-xs font-semibold text-slate-600">{fl.label}</Label>
                                <FieldInput field={fl} value={form[fl.name]} onChange={(v) => setForm((p) => ({ ...p, [fl.name]: v }))} />
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
                        <button onClick={save} disabled={saving} data-testid="save-entity-btn" className="px-5 py-2.5 rounded-md bg-clay text-white text-sm font-bold hover:bg-clay-dark transition-colors disabled:opacity-60">{saving ? "Enregistrement…" : "Enregistrer"}</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>Cette action est définitive.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function InscriptionsManager() {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
    const [steps, setSteps] = useState(null);
    const [saving, setSaving] = useState(false);
    const current = steps ?? (data?.inscription_steps || []);

    const update = (i, v) => { const c = [...current]; c[i] = v; setSteps(c); };
    const add = () => setSteps([...current, ""]);
    const remove = (i) => setSteps(current.filter((_, idx) => idx !== i));
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/settings", { ...(data || {}), inscription_steps: current.filter((s) => s.trim()) });
            toast.success("Étapes enregistrées");
            qc.invalidateQueries({ queryKey: ["settings"] });
        } catch { toast.error("Erreur"); }
        finally { setSaving(false); }
    };

    return (
        <div>
            <h2 className="font-display font-extrabold text-xl text-navy mb-1">Étapes d'inscription</h2>
            <p className="text-sm text-slate-500 mb-5">Ces étapes s'affichent sur la page Inscriptions du site.</p>
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-3 max-w-2xl">
                {current.map((s, i) => (
                    <div key={i} className="flex items-start gap-3" data-testid="inscription-step-row">
                        <span className="shrink-0 grid place-items-center h-8 w-8 rounded-full bg-navy text-white font-mono font-bold text-sm mt-1">{i + 1}</span>
                        <Textarea data-testid={`inscription-step-${i}`} className="flex-1" rows={2} value={s} onChange={(e) => update(i, e.target.value)} />
                        <button onClick={() => remove(i)} data-testid={`remove-step-${i}`} className="shrink-0 grid place-items-center h-9 w-9 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors mt-1" aria-label="Supprimer l'étape">
                            <Trash size={18} />
                        </button>
                    </div>
                ))}
                <div className="flex items-center justify-between pt-2">
                    <button onClick={add} data-testid="add-step-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-clay transition-colors">
                        <Plus weight="bold" size={16} /> Ajouter une étape
                    </button>
                    <button onClick={save} disabled={saving} data-testid="save-steps-btn" className="px-5 py-2.5 rounded-md bg-clay text-white text-sm font-bold hover:bg-clay-dark transition-colors disabled:opacity-60">{saving ? "Enregistrement…" : "Enregistrer les étapes"}</button>
                </div>
            </div>
        </div>
    );
}

function DocumentsManager() {
    const qc = useQueryClient();
    const { data: docs = [] } = useQuery({ queryKey: ["documents"], queryFn: async () => (await api.get("/documents")).data });
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", category: "Inscription" });
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toDelete, setToDelete] = useState(null);

    const save = async () => {
        if (!file) return toast.error("Sélectionnez un fichier");
        if (!form.title.trim()) return toast.error("Titre requis");
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("title", form.title);
            fd.append("description", form.description);
            fd.append("category", form.category);
            await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document ajouté");
            qc.invalidateQueries({ queryKey: ["documents"] });
            setOpen(false); setForm({ title: "", description: "", category: "Inscription" }); setFile(null);
        } catch (e) {
            toast.error(e.response?.data?.detail ? String(e.response.data.detail) : "Erreur lors de l'envoi");
        } finally { setSaving(false); }
    };

    const doDelete = async () => {
        try { await api.delete(`/admin/documents/${toDelete.id}`); toast.success("Supprimé"); qc.invalidateQueries({ queryKey: ["documents"] }); }
        catch { toast.error("Erreur"); }
        setToDelete(null);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-5">
                <h2 className="font-display font-extrabold text-xl text-navy">Documents PDF <span className="font-mono text-sm text-slate-400">({docs.length})</span></h2>
                <button onClick={() => setOpen(true)} data-testid="add-documents-btn" className="inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2.5 rounded-md hover:bg-navy-deep transition-colors">
                    <UploadSimple weight="bold" size={16} /> Téléverser
                </button>
            </div>
            {docs.length === 0 ? (
                <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">Aucun document.</div>
            ) : (
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                    {docs.map((d) => (
                        <div key={d.id} data-testid="documents-row" className="flex items-center gap-4 p-4">
                            <FilePdf weight="duotone" size={26} className="text-clay shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold text-navy truncate">{d.title} <span className="font-mono text-[10px] text-clay uppercase ml-1">{d.category}</span></div>
                                <div className="text-xs text-slate-400 truncate">{d.original_filename} · {humanSize(d.size)}</div>
                            </div>
                            <button onClick={() => setToDelete(d)} data-testid="delete-documents-btn" className="grid place-items-center h-9 w-9 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors" aria-label="Supprimer">
                                <Trash size={18} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="font-display">Téléverser un document</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Titre</Label>
                            <Input data-testid="doc-title-input" className="mt-1.5" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Dossier d'inscription 2025-2026" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Description</Label>
                            <Input className="mt-1.5" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="À compléter et déposer au club-house" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Catégorie</Label>
                            <select className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                                {["Inscription", "Tarifs", "Règlement", "Certificat médical", "Autre"].map((o) => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Fichier (PDF)</Label>
                            <input data-testid="doc-file-input" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-navy file:px-4 file:py-2 file:text-white file:font-semibold" />
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
                        <button onClick={save} disabled={saving} data-testid="save-document-btn" className="px-5 py-2.5 rounded-md bg-clay text-white text-sm font-bold hover:bg-clay-dark transition-colors disabled:opacity-60">{saving ? "Envoi…" : "Téléverser"}</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle><AlertDialogDescription>Cette action est définitive.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function SettingsManager() {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const current = form || data || {};

    const set = (k, v) => setForm({ ...current, [k]: v });
    const save = async () => {
        if (!data) { toast.error("Réglages en cours de chargement, réessayez."); return; }
        setSaving(true);
        try { await api.put("/admin/settings", current); toast.success("Informations enregistrées"); qc.invalidateQueries({ queryKey: ["settings"] }); }
        catch { toast.error("Erreur"); }
        finally { setSaving(false); }
    };

    const F = ({ k, label, ph, ta }) => (
        <div>
            <Label className="text-xs font-semibold text-slate-600">{label}</Label>
            {ta ? (
                <Textarea data-testid={`settings-${k}`} className="mt-1.5" rows={3} value={current[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} />
            ) : (
                <Input data-testid={`settings-${k}`} className="mt-1.5" value={current[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} />
            )}
        </div>
    );

    return (
        <div className="max-w-2xl">
            <h2 className="font-display font-extrabold text-xl text-navy mb-5">Informations du club</h2>
            <div className="grid gap-4 bg-white border border-slate-200 rounded-lg p-6">
                <F k="season_title" label="Titre de la saison" ph="Saison 2025 - 2026" />
                <F k="intro" label="Présentation du club" ph="Le TC La Turballe…" ta />
                <F k="season_info" label="Info saison / inscriptions" ph="Les inscriptions sont ouvertes…" ta />
                <div className="grid sm:grid-cols-2 gap-4">
                    <F k="tenup_url" label="Lien Ten'Up" ph="https://tenup.fft.fr" />
                    <F k="tenup_offers_url" label="Lien Ten'Up — Offres du club" ph="https://tenup.fft.fr/club/61440326/offres" />
                    <F k="tenup_championship_url" label="Lien Ten'Up — Championnat (page Compétitions)" ph="https://tenup.fft.fr/..." />
                    <F k="facebook_url" label="Lien Facebook" ph="https://facebook.com/…" />
                    <F k="instagram_url" label="Lien Instagram" ph="https://instagram.com/…" />
                    <F k="email" label="Email de contact" ph="contact@tclt.fr" />
                    <F k="phone" label="Téléphone" ph="02 40 00 00 00" />
                    <F k="address" label="Adresse" ph="44420 La Turballe" />
                </div>
                <F k="hours" label="Horaires" ph="Lundi au samedi : 9h - 22h" />
                <div className="flex justify-end pt-2">
                    <button onClick={save} disabled={saving || !data} data-testid="save-settings-btn" className="px-6 py-2.5 rounded-md bg-clay text-white text-sm font-bold hover:bg-clay-dark transition-colors disabled:opacity-60">{saving ? "Enregistrement…" : "Enregistrer"}</button>
                </div>
            </div>
        </div>
    );
}

function PhotoManager() {
    const qc = useQueryClient();
    const { data: photos = [] } = useQuery({ queryKey: ["photos"], queryFn: async () => (await api.get("/photos")).data });
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", category: "Ambiance" });
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const [editing, setEditing] = useState(null);

    const openNew = () => { setEditing(null); setForm({ title: "", description: "", category: "Ambiance" }); setFile(null); setOpen(true); };
    const openEdit = (p) => { setEditing(p); setForm({ title: p.title || "", description: p.description || "", category: p.category || "Ambiance" }); setFile(null); setOpen(true); };

    const save = async () => {
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/admin/photos/${editing.id}`, { title: form.title, description: form.description, category: form.category, order: editing.order || 0 });
            } else {
                if (!file) { toast.error("Sélectionnez une image"); setSaving(false); return; }
                const fd = new FormData();
                fd.append("file", file);
                fd.append("title", form.title);
                fd.append("description", form.description);
                fd.append("category", form.category);
                await api.post("/admin/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success(editing ? "Photo modifiée" : "Photo ajoutée");
            qc.invalidateQueries({ queryKey: ["photos"] });
            setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail ? String(e.response.data.detail) : "Erreur lors de l'envoi");
        } finally { setSaving(false); }
    };

    const doDelete = async () => {
        try { await api.delete(`/admin/photos/${toDelete.id}`); toast.success("Supprimée"); qc.invalidateQueries({ queryKey: ["photos"] }); }
        catch { toast.error("Erreur"); }
        setToDelete(null);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-display font-extrabold text-xl text-navy">Galerie photos <span className="font-mono text-sm text-slate-400">({photos.length})</span></h2>
                <button onClick={openNew} data-testid="add-photos-btn" className="inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2.5 rounded-md hover:bg-navy-deep transition-colors">
                    <UploadSimple weight="bold" size={16} /> Ajouter une photo
                </button>
            </div>
            <p className="text-sm text-slate-500 mb-5">Ajoutez vos photos avec un titre et un commentaire. Elles apparaissent sur la page Galerie du site.</p>

            {photos.length === 0 ? (
                <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">Aucune photo pour le moment.</div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {photos.map((p) => (
                        <div key={p.id} data-testid="photos-row" className="brand-card overflow-hidden group">
                            <div className="aspect-square bg-slate-100 overflow-hidden">
                                <img src={photoImageUrl(p.id)} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
                            </div>
                            <div className="p-3">
                                <span className="font-mono text-[9px] uppercase tracking-widest text-clay">{p.category}</span>
                                <div className="text-sm font-semibold text-navy truncate">{p.title || "Sans titre"}</div>
                                <div className="mt-2 flex items-center gap-1">
                                    <button onClick={() => openEdit(p)} data-testid="edit-photos-btn" className="grid place-items-center h-8 w-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-navy transition-colors" aria-label="Modifier"><PencilSimple size={16} /></button>
                                    <button onClick={() => setToDelete(p)} data-testid="delete-photos-btn" className="grid place-items-center h-8 w-8 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors" aria-label="Supprimer"><Trash size={16} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier la photo" : "Ajouter une photo"}</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                        {!editing && (
                            <div>
                                <Label className="text-xs font-semibold text-slate-600">Image</Label>
                                <input data-testid="photo-file-input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-navy file:px-4 file:py-2 file:text-white file:font-semibold" />
                            </div>
                        )}
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Titre</Label>
                            <Input data-testid="photo-title-input" className="mt-1.5" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Ex. Finale du tournoi interne" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Commentaire</Label>
                            <Textarea data-testid="photo-description-input" className="mt-1.5" rows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Quelques mots sur la photo…" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Catégorie</Label>
                            <select data-testid="photo-category-select" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                                {["Courts", "Équipes", "Ambiance"].map((o) => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
                        <button onClick={save} disabled={saving} data-testid="save-photo-btn" className="px-5 py-2.5 rounded-md bg-clay text-white text-sm font-bold hover:bg-clay-dark transition-colors disabled:opacity-60">{saving ? "Envoi…" : "Enregistrer"}</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Supprimer cette photo ?</AlertDialogTitle><AlertDialogDescription>Cette action est définitive.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function EventManager() {
    const qc = useQueryClient();
    const { data: events = [] } = useQuery({ queryKey: ["events-admin"], queryFn: async () => (await api.get("/events?all=true")).data });
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ title: "", description: "", date: "", location: "", published: true, order: 0 });
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const [regsFor, setRegsFor] = useState(null);

    const { data: regs = [] } = useQuery({
        queryKey: ["event-regs", regsFor?.id],
        queryFn: async () => (await api.get(`/admin/events/${regsFor.id}/registrations`)).data,
        enabled: !!regsFor,
    });

    const openNew = () => { setEditing(null); setForm({ title: "", description: "", date: "", location: "", published: true, order: 0 }); setFile(null); setOpen(true); };
    const openEdit = (ev) => { setEditing(ev); setForm({ title: ev.title, description: ev.description || "", date: ev.date ? toLocalInput(ev.date) : "", location: ev.location || "", published: ev.published, order: ev.order || 0 }); setFile(null); setOpen(true); };

    const save = async () => {
        if (!form.title?.trim()) { toast.error("Titre requis"); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            if (file) fd.append("file", file);
            fd.append("title", form.title);
            fd.append("description", form.description || "");
            fd.append("date", form.date ? new Date(form.date).toISOString() : "");
            fd.append("location", form.location || "");
            fd.append("published", form.published ? "true" : "false");
            fd.append("order", String(form.order || 0));
            const cfg = { headers: { "Content-Type": "multipart/form-data" } };
            if (editing) await api.put(`/admin/events/${editing.id}`, fd, cfg);
            else await api.post(`/admin/events`, fd, cfg);
            toast.success(editing ? "Événement modifié" : "Événement ajouté");
            qc.invalidateQueries({ queryKey: ["events-admin"] });
            qc.invalidateQueries({ queryKey: ["events"] });
            setOpen(false);
        } catch (e) { toast.error(e.response?.data?.detail ? String(e.response.data.detail) : "Erreur lors de l'enregistrement"); }
        finally { setSaving(false); }
    };

    const doDelete = async () => {
        try { await api.delete(`/admin/events/${toDelete.id}`); toast.success("Supprimé"); qc.invalidateQueries({ queryKey: ["events-admin"] }); qc.invalidateQueries({ queryKey: ["events"] }); }
        catch { toast.error("Erreur"); }
        setToDelete(null);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-display font-extrabold text-xl text-navy">Événements <span className="font-mono text-sm text-slate-400">({events.length})</span></h2>
                <button onClick={openNew} data-testid="add-events-btn" className="inline-flex items-center gap-2 bg-navy text-white text-sm font-bold px-4 py-2.5 rounded-md hover:bg-navy-deep transition-colors">
                    <Plus weight="bold" size={16} /> Ajouter un événement
                </button>
            </div>
            <p className="text-sm text-slate-500 mb-5">Ajoutez une affiche, la description et récupérez les inscriptions reçues.</p>

            {events.length === 0 ? (
                <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">Aucun événement.</div>
            ) : (
                <div className="space-y-3">
                    {events.map((ev) => (
                        <div key={ev.id} data-testid="events-row" className="flex items-center gap-4 brand-card p-4">
                            <div className="shrink-0 h-16 w-16 rounded-lg bg-slate-100 overflow-hidden grid place-items-center">
                                {ev.storage_path ? <img src={eventPosterUrl(ev.id)} alt={ev.title} className="h-full w-full object-cover" /> : <Confetti weight="duotone" size={24} className="text-slate-300" />}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-navy truncate">{ev.title}</span>
                                    {!ev.published && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 rounded">Brouillon</span>}
                                </div>
                                <div className="text-xs text-slate-400">{ev.date ? frDate(ev.date, { weekday: "long" }) : "Date à définir"}{ev.location ? ` · ${ev.location}` : ""}</div>
                            </div>
                            <button onClick={() => setRegsFor(ev)} data-testid="view-regs-btn" className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-navy border border-slate-200 rounded-md px-3 py-2 hover:border-navy transition-colors">
                                <UsersThree size={16} /> Inscriptions
                            </button>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => openEdit(ev)} data-testid="edit-events-btn" className="grid place-items-center h-9 w-9 rounded-md text-slate-500 hover:bg-slate-100 hover:text-navy transition-colors" aria-label="Modifier"><PencilSimple size={18} /></button>
                                <button onClick={() => setToDelete(ev)} data-testid="delete-events-btn" className="grid place-items-center h-9 w-9 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors" aria-label="Supprimer"><Trash size={18} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier l'événement" : "Ajouter un événement"}</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Affiche (image)</Label>
                            <input data-testid="event-file-input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-navy file:px-4 file:py-2 file:text-white file:font-semibold" />
                            {editing && editing.storage_path && !file && <p className="text-xs text-slate-400 mt-1">Une affiche est déjà en place. Choisissez un fichier pour la remplacer.</p>}
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Titre</Label>
                            <Input data-testid="event-title-input" className="mt-1.5" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Tournoi interne, portes ouvertes…" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-slate-600">Description</Label>
                            <Textarea className="mt-1.5" rows={4} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Programme, horaires, informations…" />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-semibold text-slate-600">Date & heure</Label>
                                <Input type="datetime-local" className="mt-1.5" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-slate-600">Lieu</Label>
                                <Input className="mt-1.5" value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} placeholder="Club-house, courts…" />
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch checked={!!form.published} onCheckedChange={(v) => setForm((p) => ({ ...p, published: v }))} />
                            <span className="text-sm text-slate-500">{form.published ? "Publié (visible sur le site)" : "Brouillon"}</span>
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
                        <button onClick={save} disabled={saving} data-testid="save-event-btn" className="px-5 py-2.5 rounded-md bg-clay text-white text-sm font-bold hover:bg-clay-dark transition-colors disabled:opacity-60">{saving ? "Enregistrement…" : "Enregistrer"}</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!regsFor} onOpenChange={(v) => !v && setRegsFor(null)}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-display">Inscriptions — {regsFor?.title}</DialogTitle></DialogHeader>
                    <div className="py-2">
                        {regs.length === 0 ? (
                            <p className="text-slate-500 text-sm py-6 text-center">Aucune inscription pour le moment.</p>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {regs.map((r) => (
                                    <div key={r.id} data-testid="registration-row" className="py-3">
                                        <div className="flex items-center justify-between">
                                            <span className="font-semibold text-navy">{r.name}</span>
                                            <span className="text-xs font-mono text-slate-400">{r.participants} pers.</span>
                                        </div>
                                        <div className="text-sm text-slate-500">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
                                        {r.message && <div className="text-sm text-slate-500 mt-1 italic">« {r.message} »</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Supprimer cet événement ?</AlertDialogTitle><AlertDialogDescription>Les inscriptions liées seront aussi supprimées. Action définitive.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();

    return (
        <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
            <header className="bg-navy-deep text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="TCLT" className="h-10 w-10 object-contain" />
                        <div>
                            <div className="font-display font-extrabold leading-none">Espace Admin</div>
                            <div className="text-[11px] text-slate-400 mt-0.5">{user?.email}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/" target="_blank" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-clay px-3 py-2">
                            <ArrowSquareOut size={16} /> <span className="hidden sm:inline">Voir le site</span>
                        </Link>
                        <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-sm font-bold bg-white/10 hover:bg-clay px-3 py-2 rounded-md transition-colors">
                            <SignOut size={16} /> <span className="hidden sm:inline">Déconnexion</span>
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
                <Tabs defaultValue="news">
                    <TabsList className="mb-6 flex-wrap h-auto">
                        <TabsTrigger value="news" data-testid="tab-news" className="gap-2"><Newspaper size={16} /> Actualités</TabsTrigger>
                        <TabsTrigger value="offers" data-testid="tab-offers" className="gap-2"><Tag size={16} /> Offre du club</TabsTrigger>
                        <TabsTrigger value="championships" data-testid="tab-championships" className="gap-2"><Trophy size={16} /> Compétitions</TabsTrigger>
                        <TabsTrigger value="events" data-testid="tab-events" className="gap-2"><Confetti size={16} /> Événements</TabsTrigger>
                        <TabsTrigger value="inscriptions" data-testid="tab-inscriptions" className="gap-2"><Ticket size={16} /> Inscriptions</TabsTrigger>
                        <TabsTrigger value="gallery" data-testid="tab-gallery" className="gap-2"><Images size={16} /> Galerie</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="tab-settings" className="gap-2"><GearSix size={16} /> Infos club</TabsTrigger>
                    </TabsList>

                    <TabsContent value="news">
                        <EntityManager
                            title="Actualités" singular="une actualité" queryKey="news" endpoint="news"
                            empty="Aucune actualité." defaults={{ title: "", excerpt: "", content: "", category: "Actualité", image_url: "", published: true }}
                            fields={[
                                { name: "title", label: "Titre", required: true },
                                { name: "category", label: "Catégorie", type: "select", options: ["Actualité", "Club", "Événement", "Compétition", "École de tennis"] },
                                { name: "excerpt", label: "Résumé", type: "textarea", rows: 2 },
                                { name: "content", label: "Contenu", type: "textarea", rows: 6 },
                                { name: "image_url", label: "URL de l'image (optionnel)", placeholder: "https://…" },
                                { name: "published", label: "Statut", type: "switch" },
                            ]}
                            columns={(it) => (
                                <>
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-[10px] uppercase text-clay">{it.category}</span>
                                        {!it.published && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 rounded">Brouillon</span>}
                                    </div>
                                    <div className="font-semibold text-navy truncate">{it.title}</div>
                                    <div className="text-xs text-slate-400">{frDate(it.date)}</div>
                                </>
                            )}
                        />
                    </TabsContent>

                    <TabsContent value="offers">
                        <EntityManager
                            title="Offre du club" singular="une offre" queryKey="offers" endpoint="offers"
                            empty="Aucune offre." defaults={{ title: "", public: "Tous", description: "", price: 0, tenup_url: "", order: 0 }}
                            fields={[
                                { name: "title", label: "Titre de l'offre", placeholder: "Adhésion, Cours adultes 1h30…" },
                                { name: "public", label: "Public", type: "select", options: ["Tous", "Adulte", "Jeune"] },
                                { name: "description", label: "Descriptif", type: "textarea", rows: 3 },
                                { name: "price", label: "Prix (€ / saison)", type: "number" },
                                { name: "tenup_url", label: "Lien Ten'Up (optionnel)", placeholder: "https://tenup.fft.fr/club/61440326/offres/…" },
                                { name: "order", label: "Ordre d'affichage", type: "number" },
                            ]}
                            columns={(it) => (
                                <>
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-[10px] uppercase text-clay">{it.public}</span>
                                        <span className="text-xs font-bold text-navy">{it.price ? `${it.price} €` : "Gratuit"}</span>
                                    </div>
                                    <div className="font-semibold text-navy truncate">{it.title}</div>
                                    <div className="text-xs text-slate-400 truncate">{it.tenup_url ? "Lien Ten'Up ✓" : "Sans lien"} · {it.description}</div>
                                </>
                            )}
                        />
                    </TabsContent>

                    <TabsContent value="championships">
                        <EntityManager
                            title="Compétitions" singular="une rencontre" queryKey="championships" endpoint="championships"
                            empty="Aucune rencontre." defaults={{ team_name: "", category: "Séniors Hommes", division: "", date: new Date().toISOString(), opponent: "", location: "La Turballe", home_away: "Domicile", result: "À venir", score: "" }}
                            fields={[
                                { name: "team_name", label: "Équipe" },
                                { name: "category", label: "Catégorie", type: "select", options: ["Séniors Hommes", "Séniors Femmes", "Jeunes", "+35 ans", "+45 ans"] },
                                { name: "division", label: "Division / poule", placeholder: "Départementale 2" },
                                { name: "date", label: "Date & heure", type: "datetime" },
                                { name: "opponent", label: "Adversaire" },
                                { name: "home_away", label: "Domicile / Extérieur", type: "select", options: ["Domicile", "Extérieur"] },
                                { name: "location", label: "Lieu" },
                                { name: "result", label: "Résultat", type: "select", options: ["À venir", "Victoire", "Défaite", "Match nul"] },
                                { name: "score", label: "Score (ex. 4 - 2)", placeholder: "4 - 2" },
                            ]}
                            columns={(it) => (
                                <>
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-[10px] uppercase text-clay">{it.category}</span>
                                        <span className="text-xs text-slate-400">{it.home_away}</span>
                                        {it.result && it.result !== "À venir" && <span className="text-[10px] font-bold text-navy">{it.result}{it.score ? ` ${it.score}` : ""}</span>}
                                    </div>
                                    <div className="font-semibold text-navy truncate">{it.team_name} vs {it.opponent || "?"}</div>
                                    <div className="text-xs text-slate-400">{frDate(it.date, { weekday: "long" })} · {it.location}</div>
                                </>
                            )}
                        />
                    </TabsContent>

                    <TabsContent value="inscriptions">
                        <div className="space-y-10">
                            <InscriptionsManager />
                            <DocumentsManager />
                        </div>
                    </TabsContent>
                    <TabsContent value="gallery"><PhotoManager /></TabsContent>
                    <TabsContent value="events"><EventManager /></TabsContent>
                    <TabsContent value="settings"><SettingsManager /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
